import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Organization, Member, Notifications } from '../types/models';
import {
  getOrganizations, getMembers, getNotifications, markSectionSeen,
} from '../services/ApiService';
import { useAuth } from './AuthContext';
import { setOrganization } from '../services/AnalyticsService';

interface OrganizationContextType {
  organizations: Organization[];
  currentOrg: Organization | null;
  members: Member[];
  isBoardMember: boolean;
  notifications: Notifications | null;
  setCurrentOrgId: (id: string) => void;
  refreshOrganizations: () => Promise<void>;
  refreshMembers: () => Promise<void>;
  updateLastSeen: (section: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | null>(null);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(
    () => localStorage.getItem('ra_current_org')
  );
  const [members, setMembers] = useState<Member[]>([]);
  const [notifications, setNotifications] = useState<Notifications | null>(null);

  const currentOrg = organizations.find(o => o.id === currentOrgId) ?? organizations[0] ?? null;
  const isBoardMember = currentOrg?.myRole === 'board_member';

  const refreshOrganizations = useCallback(async () => {
    try {
      const orgs = await getOrganizations();
      setOrganizations(orgs);
    } catch {
      setOrganizations([]);
    }
  }, []);

  const refreshMembers = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const m = await getMembers(currentOrg.id);
      setMembers(m);
    } catch {
      setMembers([]);
    }
  }, [currentOrg]);

  const refreshNotifications = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const n = await getNotifications(currentOrg.id);
      setNotifications(n);
    } catch {
      setNotifications(null);
    }
  }, [currentOrg]);

  const setCurrentOrgId = useCallback((id: string) => {
    setCurrentOrgIdState(id);
    localStorage.setItem('ra_current_org', id);
  }, []);

  const updateLastSeen = useCallback(async (section: string) => {
    if (!currentOrg) return;
    await markSectionSeen(currentOrg.id, section);
    await refreshNotifications();
  }, [currentOrg, refreshNotifications]);

  useEffect(() => {
    if (user) refreshOrganizations();
    else {
      setOrganizations([]);
      setMembers([]);
      setNotifications(null);
    }
  }, [user, refreshOrganizations]);

  useEffect(() => {
    if (currentOrg) setOrganization(currentOrg);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, currentOrg?.subscriptionTier, currentOrg?.myRole, currentOrg?.memberCount]);

  useEffect(() => {
    if (currentOrg) {
      refreshMembers();
      refreshNotifications();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <OrganizationContext.Provider value={{
      organizations, currentOrg, members, isBoardMember,
      notifications, setCurrentOrgId, refreshOrganizations,
      refreshMembers, updateLastSeen, refreshNotifications,
    }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const ctx = useContext(OrganizationContext);
  if (!ctx) throw new Error('useOrganization must be used within OrganizationProvider');
  return ctx;
}
